import React from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  CheckCircle2,
  AlertTriangle,
  Info,
  X as CloseIcon,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

/**
 * Compact toast card with title + description and accent stripe
 * Supports dark/light themes automatically.
 */
const ToastCard = ({ type = "info", title, message, onClose }) => {
  const typeStyles = {
    success: {
      accent: "bg-green-600 dark:bg-green-500",
      iconColor: "text-green-600 dark:text-green-400",
      bg: "bg-white dark:bg-acs-dark-surface",
      border: "border-gray-200 dark:border-acs-dark-border",
      textTitle: "text-gray-900 dark:text-acs-dark-text",
      textMessage: "text-gray-700 dark:text-acs-dark-subtext",
      close: "text-gray-400 dark:text-acs-dark-subtext hover:text-gray-600 dark:hover:text-white",
    },
    error: {
      accent: "bg-red-600 dark:bg-red-500",
      iconColor: "text-red-600 dark:text-red-400",
      bg: "bg-white dark:bg-acs-dark-surface",
      border: "border-gray-200 dark:border-acs-dark-border",
      textTitle: "text-gray-900 dark:text-acs-dark-text",
      textMessage: "text-gray-700 dark:text-acs-dark-subtext",
      close: "text-gray-400 dark:text-acs-dark-subtext hover:text-gray-600 dark:hover:text-white",
    },
    info: {
      accent: "bg-blue-600 dark:bg-[#3B5CCC]",
      iconColor: "text-blue-600 dark:text-[#3B5CCC]",
      bg: "bg-white dark:bg-acs-dark-surface",
      border: "border-gray-200 dark:border-acs-dark-border",
      textTitle: "text-gray-900 dark:text-acs-dark-text",
      textMessage: "text-gray-700 dark:text-acs-dark-subtext",
      close: "text-gray-400 dark:text-acs-dark-subtext hover:text-gray-600 dark:hover:text-white",
    },
    warning: {
      accent: "bg-amber-500 dark:bg-amber-400",
      iconColor: "text-amber-600 dark:text-amber-300",
      bg: "bg-white dark:bg-acs-dark-surface",
      border: "border-gray-200 dark:border-acs-dark-border",
      textTitle: "text-gray-900 dark:text-acs-dark-text",
      textMessage: "text-gray-700 dark:text-acs-dark-subtext",
      close: "text-gray-400 dark:text-acs-dark-subtext hover:text-gray-600 dark:hover:text-white",
    },
  }[type];

  const Icon =
    type === "success"
      ? CheckCircle2
      : type === "error"
      ? AlertTriangle
      : type === "warning"
      ? AlertTriangle
      : Info;

  return (
    <div
      className={`relative flex w-full items-start gap-fluid-1.5 p-fluid-2 pr-fluid-3 rounded-lg shadow-md border ${typeStyles.border} ${typeStyles.bg} animate-slide-in`}
      role="status"
    >
      {/* Accent stripe */}
      <div className={`absolute left-0 top-0 h-full w-[3px] ${typeStyles.accent}`} />

      {/* Icon */}
      <Icon
        className={`w-fluid-icon-sm h-fluid-icon-sm ${typeStyles.iconColor} mt-0.5 shrink-0`}
      />

      {/* Text */}
      <div className="flex-1 min-w-0">
        {title ? (
          <div
            className={`text-dense-base font-semibold leading-tight ${typeStyles.textTitle}`}
          >
            {title}
          </div>
        ) : null}
        {message ? (
          <div
            className={`text-fluid-sm leading-snug ${typeStyles.textMessage}`}
          >
            {message}
          </div>
        ) : null}
      </div>

      {/* Close */}
      <button
        type="button"
        aria-label="Close"
        className={`ml-fluid-1 transition-colors ${typeStyles.close}`}
        onClick={onClose}
      >
        <CloseIcon className="w-fluid-icon-xs h-fluid-icon-xs" />
      </button>
    </div>
  );
};

/** Shared base options */
const base = {
  position: "top-right",
  autoClose: 3000,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "light", // handled manually via dark classes
  hideProgressBar: true,
  icon: false,
  closeButton: false,
};

export const notify = {
  success: ({ title = "Success", message = "", options } = {}) =>
    toast(({ closeToast }) => (
      <ToastCard
        type="success"
        title={title}
        message={message}
        onClose={closeToast}
      />
    ), { ...base, ...options }),

  error: ({ title = "Something went wrong", message = "", options } = {}) =>
    toast(({ closeToast }) => (
      <ToastCard
        type="error"
        title={title}
        message={message}
        onClose={closeToast}
      />
    ), { ...base, ...options }),

  info: ({ title = "", message = "", options } = {}) =>
    toast(({ closeToast }) => (
      <ToastCard
        type="info"
        title={title}
        message={message}
        onClose={closeToast}
      />
    ), { ...base, ...options }),

  warning: ({ title = "Warning", message = "", options } = {}) =>
    toast(({ closeToast }) => (
      <ToastCard
        type="warning"
        title={title}
        message={message}
        onClose={closeToast}
      />
    ), { ...base, ...options }),
};

export const AppToastContainer = () => (
  <ToastContainer
    limit={3}
    newestOnTop
    style={{ zIndex: 9999 }}
    toastClassName="p-0 bg-transparent shadow-none"
    bodyClassName="m-0"
  />
);

export default notify;
